import React, { createContext, useContext, useMemo } from 'react';

import { globalCategoriesUrl } from '~/components/SystemConfiguration/GlobalConfiguration/types';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type { GlobalPolicyCategoryInfoList, PolicyCategoryInfoList } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

export interface CategoryLimitOption {
  id: string;
  value: string;
  label: string;
  isGroupHeader?: boolean;
}

export interface FetchedPolicyCategories {
  /** The ENABLED categories, as multiselect options. */
  options: CategoryLimitOption[];
  /** The keys of ALL of them, enabled or not, across BOTH policy-category tiers. */
  keys: string[];
  isLoading: boolean;
  isError: boolean;
}

const PolicyCategoriesOptionsContext = createContext<CategoryLimitOption[]>([]);

/** Reads the configuration's policy categories. Called once per FNOL-configuration screen
 *  (dozens of field cards consume the options — a per-card fetch would hammer the endpoint
 *  with identical requests), which also lets the screen feed `keys` to the on-the-fly
 *  incident-category migration before Formik initialises. */
export const useFetchPolicyCategories = (incidentConfigurationId: number): FetchedPolicyCategories => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const isPolicyCategoriesEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.POLICY_CATEGORIES);

  const { data, isLoading, isError } = useDataFetcher<PolicyCategoryInfoList>(
    `/sysconfig/api/v1/organizations/${organization.id}/incident_configurations/${incidentConfigurationId}/policy_categories`,
    {},
    { shouldFetch: isPolicyCategoriesEnabled }
  );

  // The organization-wide tier, fetched alongside the per-configuration one. Both tiers share
  // ONE key namespace, so a key from either must reach `keys`: a projected global field carries
  // its category scope into this blob, so an org-scoped key lands in a limit list here without
  // ever being selected on this screen. Left out, it is fabricated as a phantom incident
  // category - editable and deletable in the categories dialog, and rejected by the BE's
  // namespace check on save.
  const isGlobalCategoriesEnabled = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.GLOBAL_CONFIGURED_FIELDS
  );
  const {
    data: globalData,
    isLoading: isGlobalLoading,
    isError: isGlobalError,
  } = useDataFetcher<GlobalPolicyCategoryInfoList>(
    globalCategoriesUrl(organization.id),
    {},
    { shouldFetch: isGlobalCategoriesEnabled }
  );

  return useMemo(() => {
    // Array.isArray, not just ??: while the fetch is in flight (or on an error payload) the
    // fetcher's data is not a list, and this must degrade to empty rather than throw.
    const fetched = isPolicyCategoriesEnabled && Array.isArray(data) ? data : [];
    const fetchedGlobal = isGlobalCategoriesEnabled && Array.isArray(globalData) ? globalData : [];
    return {
      // Offer only ENABLED categories: the BE rejects a save that newly references a disabled
      // key, so offering one would invite a guaranteed save error. Stale selections that
      // already reference a disabled/removed key still render as chips (removable), because
      // the chips component falls back to the raw key for values with no matching option.
      options: fetched
        .filter(({ is_enabled }) => is_enabled)
        .map(({ key, display_name }) => ({ id: key, value: key, label: display_name })),
      // ALL keys, disabled ones included: a policy key selected in a limit list must never be
      // fabricated into a phantom incident category, and a category can be disabled after it
      // was selected.
      keys: [...fetched, ...fetchedGlobal].map(({ key }) => key),
      isLoading: isLoading || isGlobalLoading,
      isError: isError || isGlobalError,
    };
  }, [
    data,
    globalData,
    isPolicyCategoriesEnabled,
    isGlobalCategoriesEnabled,
    isLoading,
    isGlobalLoading,
    isError,
    isGlobalError,
  ]);
};

export const PolicyCategoriesOptionsProvider: React.FC<{
  options: CategoryLimitOption[];
  children?: React.ReactNode;
}> = ({ options, children }) => (
  <PolicyCategoriesOptionsContext.Provider value={options}>{children}</PolicyCategoriesOptionsContext.Provider>
);

/** The policy-category options for "Limit to Categories" — [] when the flag is off or
 *  outside the provider (e.g. the policy-configuration screen, which shares the field
 *  cards but has no incident categories UI). */
export const usePolicyCategoryOptions = (): CategoryLimitOption[] => useContext(PolicyCategoriesOptionsContext);

/** A group's header row followed by its members, or nothing at all when the group is empty —
 *  a header with no rows beneath it is just a stray label. */
const withGroupHeader = (id: string, label: string, categories: CategoryLimitOption[]): CategoryLimitOption[] =>
  categories.length ? [{ id, value: id, label, isGroupHeader: true }, ...categories] : [];

/** One grouped options list for the "Limit to Categories" multiselect. Without policy
 *  categories the incident list passes through untouched (no headers) — flag-off renders
 *  byte-identical to today. */
export const buildCategoryLimitOptions = (
  incidentCategories: CategoryLimitOption[],
  policyCategories: CategoryLimitOption[]
): CategoryLimitOption[] => {
  if (!policyCategories.length) {
    return incidentCategories;
  }
  return [
    ...withGroupHeader('__incident_categories_header', 'Incident categories', incidentCategories),
    ...withGroupHeader('__policy_categories_header', 'Policy categories', policyCategories),
  ];
};
